import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { useAuthStore } from "@/store/auth";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export const mediaKeys = {
  list: (propertyId: string) => ["properties", propertyId, "media"] as const,
};

export function usePropertyMedia(propertyId: string | undefined) {
  return useQuery({
    queryKey: mediaKeys.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/properties/{property_id}/media",
        { params: { path: { property_id: propertyId! } } },
      );
      if (error || !data) throw new Error("Failed to load media");
      return data;
    },
  });
}

export function useUploadMedia(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      // Multipart upload via raw fetch (openapi-fetch is JSON-oriented).
      const form = new FormData();
      form.append("file", file);
      form.append("category", "photo");
      const token = useAuthStore.getState().token;
      const res = await fetch(
        `${BASE_URL}/api/v1/properties/${propertyId}/media`,
        {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: form,
        },
      );
      if (!res.ok) throw new Error("Upload failed");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mediaKeys.list(propertyId) }),
  });
}

export function useDeleteMedia(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: string) => {
      const { error } = await apiClient.DELETE(
        "/api/v1/properties/{property_id}/media/{media_id}",
        { params: { path: { property_id: propertyId, media_id: mediaId } } },
      );
      if (error) throw new Error("Delete failed");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mediaKeys.list(propertyId) }),
  });
}
